"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useTheme } from "@/hooks/useTheme"

const schema = z.object({
  email:    z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
})
type FormData = z.infer<typeof schema>

const FONT = "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif"

const FEATURES = [
  { icon: "◎", title: "Always in your corner", text: "A companion that remembers your goals, habits, and what matters to you." },
  { icon: "✦", title: "Talk any way you like", text: "Text, voice, or video — pick up the conversation wherever you are." },
  { icon: "⬡", title: "Private by design", text: "Your conversations stay yours. Always encrypted, never shared." },
]

export default function LoginPage() {
  const router = useRouter()
  const { t, isDark, toggle } = useTheme()
  const [serverError, setServerError] = useState("")
  const [loading, setLoading]         = useState(false)

  const { register, handleSubmit, formState: { errors } } =
    useForm<FormData>({ resolver: zodResolver(schema) })

  async function onSubmit(data: FormData) {
    setLoading(true)
    setServerError("")
    const res  = await fetch("/api/auth/login", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(data),
    })
    const json = await res.json()
    setLoading(false)
    if (!res.ok) { setServerError(json.error ?? "Authentication failed"); return }
    router.push("/dashboard")
    router.refresh()
  }

  const emailField    = register("email")
  const passwordField = register("password")

  const inputStyle = {
    width: "100%", height: 46,
    backgroundColor: t.bg,
    border: `1px solid ${t.borderStrong}`,
    borderRadius: 10,
    padding: "0 14px",
    fontSize: 15, color: t.fg,
    outline: "none",
    fontFamily: FONT,
    boxSizing: "border-box" as const,
    transition: "border-color 0.2s, box-shadow 0.2s",
  }

  const focusRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = t.accent
    e.currentTarget.style.boxShadow   = `0 0 0 3px ${t.accent}26`
  }
  const clearRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = t.borderStrong
    e.currentTarget.style.boxShadow   = "none"
  }

  return (
    <div style={{
      minHeight: "100svh",
      display: "flex",
      backgroundColor: t.bg,
      fontFamily: FONT,
      WebkitFontSmoothing: "antialiased",
    }}>
      <style>{`
        @media (max-width: 920px) { .login-brand-panel { display: none !important; } }
        @keyframes authFloat {
          0%, 100% { transform: translateY(0px) }
          50%      { transform: translateY(-18px) }
        }
        @keyframes authDrift {
          0%, 100% { transform: translate(0, 0) scale(1) }
          50%      { transform: translate(24px, -16px) scale(1.06) }
        }
      `}</style>

      {/* ── Brand panel ── */}
      <div className="login-brand-panel" style={{
        flex: "1 1 52%",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(160deg, #0f0c29 0%, #1a1a4e 45%, #24243e 100%)",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        padding: "clamp(32px, 4vw, 56px)",
      }}>
        {/* ambient glows */}
        <div style={{
          position: "absolute", top: "-20%", right: "-15%",
          width: 520, height: 520, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(109,40,217,0.35), transparent 70%)",
          pointerEvents: "none",
          animation: "authDrift 14s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", bottom: "-25%", left: "-10%",
          width: 480, height: 480, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37,99,235,0.28), transparent 70%)",
          pointerEvents: "none",
          animation: "authDrift 18s ease-in-out infinite reverse",
        }} />

        {/* dot grid */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse at 30% 40%, black 20%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at 30% 40%, black 20%, transparent 75%)",
          pointerEvents: "none",
        }} />

        {/* concentric rings */}
        <svg
          viewBox="0 0 400 400"
          style={{
            position: "absolute", right: -120, bottom: -120,
            width: 420, height: 420, pointerEvents: "none", opacity: 0.5,
          }}
        >
          {[70, 110, 150, 190].map((r) => (
            <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="1" />
          ))}
        </svg>

        {/* floating orbs */}
        <div style={{
          position: "absolute", top: "18%", right: "14%",
          width: 14, height: 14, borderRadius: "50%",
          background: "linear-gradient(135deg, #a78bfa, #6d28d9)",
          boxShadow: "0 0 18px rgba(167,139,250,0.65)",
          animation: "authFloat 7s ease-in-out infinite",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", top: "36%", right: "28%",
          width: 8, height: 8, borderRadius: "50%",
          background: "linear-gradient(135deg, #60a5fa, #2563eb)",
          boxShadow: "0 0 14px rgba(96,165,250,0.6)",
          animation: "authFloat 9s ease-in-out infinite 1.2s",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: "24%", right: "10%",
          width: 10, height: 10, borderRadius: "50%",
          background: "linear-gradient(135deg, #f0abfc, #c026d3)",
          boxShadow: "0 0 16px rgba(240,171,252,0.55)",
          animation: "authFloat 8s ease-in-out infinite 0.6s",
          pointerEvents: "none",
        }} />

        {/* logo — doubles as the way back to the landing page */}
        <Link href="/" style={{
          position: "relative", display: "flex", alignItems: "center", gap: 12,
          width: "fit-content", textDecoration: "none", opacity: 1, transition: "opacity 0.2s",
        }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.8" }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1" }}
        >
          <img src="/assets/logo.png" alt="" aria-hidden="true"
            style={{ width: 42, height: 42, objectFit: "contain", display: "block" }} />
          <span style={{ fontSize: 17, fontWeight: 700, color: "#fff", letterSpacing: "-0.01em" }}>
            Rebalancer
          </span>
        </Link>

        {/* headline + features */}
        <div style={{ position: "relative", maxWidth: 460 }}>
          <h1 style={{
            fontSize: "clamp(28px, 2.6vw, 38px)", fontWeight: 700, lineHeight: 1.15,
            color: "#fff", letterSpacing: "-0.025em", margin: "0 0 14px",
          }}>
            Your life, in balance.
          </h1>
          <p style={{
            fontSize: 16, lineHeight: 1.6, color: "rgba(255,255,255,0.6)",
            margin: "0 0 40px",
          }}>
            The personal AI companion that helps you stay on top of everything that matters.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {FEATURES.map((f) => (
              <div key={f.title} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{
                  flexShrink: 0, width: 34, height: 34, borderRadius: 9,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 15, color: "#c4b5fd",
                }}>{f.icon}</div>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: "#fff", marginBottom: 3 }}>{f.title}</div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.5, color: "rgba(255,255,255,0.55)" }}>{f.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: "relative", fontSize: 12.5, color: "rgba(255,255,255,0.35)" }}>
          © {new Date().getFullYear()} Rebalancer. All rights reserved.
        </div>
      </div>

      {/* ── Form panel ── */}
      <div style={{
        flex: "1 1 48%",
        position: "relative",
        overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "clamp(20px, 4vw, 48px)",
        backgroundColor: t.bg,
      }}>
        {/* corner glows */}
        <div style={{
          position: "absolute", top: "-18%", right: "-12%",
          width: 420, height: 420, borderRadius: "50%",
          background: `radial-gradient(circle, ${isDark ? "rgba(109,40,217,0.16)" : "rgba(109,40,217,0.08)"}, transparent 70%)`,
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: "-20%", left: "-14%",
          width: 400, height: 400, borderRadius: "50%",
          background: `radial-gradient(circle, ${isDark ? "rgba(37,99,235,0.14)" : "rgba(37,99,235,0.07)"}, transparent 70%)`,
          pointerEvents: "none",
        }} />

        {/* faint dot grid */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `radial-gradient(${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.045)"} 1px, transparent 1px)`,
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse at 70% 20%, black 10%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at 70% 20%, black 10%, transparent 70%)",
          pointerEvents: "none",
        }} />

        {/* Back to home — a pill so it reads as a control, like the toggle opposite it */}
        <Link href="/" style={{
          position: "absolute", top: 16, left: 24,
          display: "flex", alignItems: "center", gap: 6,
          height: 34, padding: "0 14px", borderRadius: 999,
          border: `1px solid ${t.border}`,
          backgroundColor: t.cardBg,
          fontSize: 13.5, fontWeight: 600, color: t.fg,
          textDecoration: "none", transition: "all 0.2s",
        }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLAnchorElement
            el.style.borderColor     = t.borderStrong
            el.style.backgroundColor = t.bg2
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLAnchorElement
            el.style.borderColor     = t.border
            el.style.backgroundColor = t.cardBg
          }}
        >
          ← Home
        </Link>

        {/* Theme toggle */}
        <button
          onClick={toggle}
          aria-label="Toggle theme"
          style={{
            position: "absolute", top: 16, right: 24,
            width: 34, height: 34, borderRadius: "50%",
            border: `1px solid ${t.border}`,
            backgroundColor: t.cardBg,
            color: t.fg,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15, transition: "all 0.2s",
          }}
        >
          {isDark ? "☀︎" : "☽"}
        </button>

        <div style={{ width: "100%", maxWidth: 384, position: "relative" }}>
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: t.fg, letterSpacing: "-0.02em", marginBottom: 8 }}>
              Welcome back
            </div>
            <div style={{ fontSize: 14.5, color: t.fg2, lineHeight: 1.5 }}>
              Sign in to your account to continue
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: t.fg, marginBottom: 7 }}>
                Email
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                style={inputStyle}
                {...emailField}
                onFocus={focusRing}
                onBlur={(e) => { void emailField.onBlur(e); clearRing(e) }}
              />
              {errors.email && <p style={{ fontSize: 12.5, color: "#ff375f", margin: "6px 0 0" }}>{errors.email.message}</p>}
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: t.fg, marginBottom: 7 }}>
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                style={inputStyle}
                {...passwordField}
                onFocus={focusRing}
                onBlur={(e) => { void passwordField.onBlur(e); clearRing(e) }}
              />
              {errors.password && <p style={{ fontSize: 12.5, color: "#ff375f", margin: "6px 0 0" }}>{errors.password.message}</p>}
            </div>

            {serverError && (
              <div style={{
                padding: "10px 14px", borderRadius: 10,
                backgroundColor: isDark ? "rgba(255,59,48,0.12)" : "rgba(255,59,48,0.06)",
                border: "1px solid rgba(255,59,48,0.25)",
              }}>
                <p style={{ fontSize: 13, color: "#ff375f", margin: 0 }}>{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", height: 46, marginTop: 6,
                borderRadius: 10, border: "none",
                cursor: loading ? "default" : "pointer",
                background: loading ? t.bg3 : t.accent,
                color: loading ? t.fg3 : "#fff",
                fontSize: 15.5, fontWeight: 600,
                fontFamily: FONT,
                letterSpacing: "-0.01em",
                transition: "all 0.2s",
                opacity: loading ? 0.7 : 1,
                boxShadow: loading ? "none" : `0 1px 2px rgba(0,0,0,0.1), 0 4px 14px ${t.accent}40`,
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.filter = "brightness(1.08)" }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = "none" }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p style={{ textAlign: "center", fontSize: 13.5, color: t.fg2, margin: "26px 0 0" }}>
            Don&apos;t have an account?{" "}
            <Link href="/register" style={{ color: t.accent, textDecoration: "none", fontWeight: 600 }}>
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
